import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';

// layouts
import DefaultLayout from '../layouts/Default';
import VerticalLayout from '../layouts/Vertical';
import HorizontalLayout from '../layouts/Horizontal/';

// components
import PrivateRoute from './PrivateRoute';
import Root from './Root';

// constants
import { LayoutTypes } from '../constants';

// hooks
import { useRedux } from '../hooks';

// lazy load all the views
// auth
const Login = React.lazy(() => import('../pages/auth/Login'));
const Confirm = React.lazy(() => import('../pages/auth/Confirm'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const ResetPassword = React.lazy(() => import('../pages/auth/ResetPassword'));
const LockScreen = React.lazy(() => import('../pages/auth/LockScreen'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));

// dashboards
const DashBoard1 = React.lazy(() => import('../pages/dashboards/DashBoard1/'));

// setting pages
const Orders = React.lazy(() => import('../pages/settings/Orders'));

// admin pages
const FranchiseDashboard = React.lazy(() => import('../pages/admin/FranchiseDashboard'));
const FranchiseDetails = React.lazy(() => import('../pages/admin/FranchiseDetails'));
const SuperAdminSettings = React.lazy(() => import('../pages/admin/SuperAdminSettings'));
const ManagePlans = React.lazy(() => import('../pages/admin/ManagePlans'));
const Invoices = React.lazy(() => import('../pages/admin/Invoices'));
const Messages = React.lazy(() => import('../pages/admin/Messages'));
const ManageStaff = React.lazy(() => import('../pages/admin/ManageStaff'));
const FranchiseSubscriptionPage = React.lazy(() => import('../pages/franchise/Subscription'));
const ManageProfile = React.lazy(() => import('../pages/profile/ManageProfile'));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
    component: React.LazyExoticComponent<React.ComponentType<any>>;
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const AllRoutes = () => {
    const { appSelector } = useRedux();

    const { layout } = appSelector((state) => ({
        layout: state.Layout,
    }));

    const getLayout = () => {
        let layoutCls: React.ComponentType = VerticalLayout;

        switch (layout.layoutType) {
            case LayoutTypes.LAYOUT_HORIZONTAL:
                layoutCls = HorizontalLayout;
                break;
            default:
                layoutCls = VerticalLayout;
                break;
        }
        return layoutCls;
    };
    let Layout = getLayout();

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'auth',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'reset-password', element: <LoadComponent component={ResetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
            ],
        },
        {
            // auth protected routes (all authenticated roles) that do not require subscription
            path: '/',
            element: <PrivateRoute component={Layout} />,
            children: [
                {
                    path: 'profile',
                    element: <LoadComponent component={ManageProfile} />,
                },
            ],
        },
        {
            // auth protected routes (all authenticated roles) that require an active subscription
            path: '/',
            element: <PrivateRoute component={Layout} requireSubscription />,
            children: [
                {
                    path: 'orders',
                    element: <LoadComponent component={Orders} />,
                },
                {
                    path: 'staff',
                    element: <LoadComponent component={ManageStaff} />,
                },
            ],
        },
        {
            // franchise admin subscription management (accessible even when unsubscribed)
            path: '/',
            element: <PrivateRoute roles={'franchise_admin'} component={Layout} />,
            children: [
                {
                    path: 'subscription',
                    element: <LoadComponent component={FranchiseSubscriptionPage} />,
                },
            ],
        },
        {
            // Messages: admins and super admins (no subscription required)
            path: '/',
            element: <PrivateRoute roles={'super_admin,franchise_admin'} component={Layout} />,
            children: [
                {
                    path: 'messages',
                    element: <LoadComponent component={Messages} />,
                },
            ],
        },
        {
            // super admin routes
            path: '/admin',
            element: <PrivateRoute roles={'super_admin'} component={Layout} />,
            children: [
                {
                    path: 'franchises',
                    element: <LoadComponent component={FranchiseDashboard} />,
                },
                {
                    path: 'franchises/:id',
                    element: <LoadComponent component={FranchiseDetails} />,
                },
                {
                    path: 'plans',
                    element: <LoadComponent component={ManagePlans} />,
                },
                {
                    path: 'invoices',
                    element: <LoadComponent component={Invoices} />,
                },
                {
                    path: 'settings',
                    element: <LoadComponent component={SuperAdminSettings} />,
                },
            ],
        },
    ]);
};

export { AllRoutes };
